import type { AgiBackTaskInfo } from '../api/agiBackTask';
import type { MatNode, MediaType } from '../store/seedanceTypes';
import { DEFAULT_SEEDANCE_MODEL } from '../store/seedanceWorkspaceStore';
import type { TaskDetailMaterialDisplay } from './taskDetailMaterialResolve';

export interface VideoRegenerateDraft {
  prompts: string[];
  nodes: MatNode[];
  model: string;
  duration: number;
  ratio: string;
  resolution: string;
}

function promptTextFromParamsJson(params: Record<string, unknown> | undefined): string {
  if (!params) return '';
  const direct = typeof params.prompt === 'string' ? params.prompt.trim() : '';
  if (direct) return direct;
  const metadata = params.metadata && typeof params.metadata === 'object'
    ? (params.metadata as Record<string, unknown>)
    : null;
  const content = Array.isArray(params.content) ? params.content : metadata?.content;
  if (!Array.isArray(content)) return '';
  for (const raw of content) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as Record<string, unknown>;
    if (String(item.type ?? '').toLowerCase() !== 'text') continue;
    const t = typeof item.text === 'string' ? item.text.trim() : '';
    if (t) return t;
  }
  return '';
}

function matNodesFromMaterials(materials: TaskDetailMaterialDisplay[]): MatNode[] {
  return materials.map((m, idx) => {
    const isDirect = /^(https?:|data:|blob:)/i.test(m.assetId);
    const url = (m.url || m.thumb || '').trim();
    const thumb = (m.thumb || m.url || '').trim();
    return {
      id: `regen_mat_${idx + 1}`,
      type: m.type as MediaType,
      name:
        m.name ||
        (m.type === 'video'
          ? `视频${idx + 1}`
          : m.type === 'audio'
            ? `音频${idx + 1}`
            : `图片${idx + 1}`),
      url: isDirect ? url || '' : '',
      thumb: isDirect ? thumb || url || '' : '',
      connected: true,
      ...(isDirect ? {} : { sourceAssetId: m.assetId }),
    };
  });
}

/** 由任务详情 + 已解析素材预览，组装「重新生成」弹窗的初始草稿（对齐工作台参数）。 */
export function buildRegenerateDraftFromTaskDetail(
  detail: AgiBackTaskInfo,
  materials: TaskDetailMaterialDisplay[],
): VideoRegenerateDraft {
  const sortedPrompts = [...(detail.prompts ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  );
  const fromRecords = sortedPrompts
    .map((p) => (typeof p.content === 'string' ? p.content.trim() : ''))
    .filter(Boolean);

  const params =
    detail.paramsJson && typeof detail.paramsJson === 'object'
      ? detail.paramsJson
      : undefined;
  const paramsMeta =
    params?.metadata && typeof params.metadata === 'object'
      ? (params.metadata as Record<string, unknown>)
      : null;

  const prompts =
    fromRecords.length > 0
      ? fromRecords
      : (() => {
          const fallback = promptTextFromParamsJson(params);
          return fallback ? [fallback] : [''];
        })();

  const ratio =
    (typeof detail.aspectRatio === 'string' && detail.aspectRatio.trim()) ||
    (typeof params?.ratio === 'string' && params.ratio.trim()) ||
    (typeof paramsMeta?.ratio === 'string' && paramsMeta.ratio.trim()) ||
    '16:9';

  const resolution =
    (typeof detail.resolution === 'string' && detail.resolution.trim()) ||
    (typeof params?.resolution === 'string' && params.resolution.trim()) ||
    (typeof paramsMeta?.resolution === 'string' && paramsMeta.resolution.trim()) ||
    '720p';

  const durationRaw =
    detail.durationSec ??
    (typeof params?.duration === 'number' ? params.duration : undefined) ??
    (typeof paramsMeta?.duration === 'number' ? paramsMeta.duration : undefined);
  const duration =
    typeof durationRaw === 'number' && durationRaw >= 5 && durationRaw <= 15
      ? durationRaw
      : 10;

  // 与工作台一致：界面使用新 Seedance 标准模型 ID。
  const model = DEFAULT_SEEDANCE_MODEL;

  return {
    prompts,
    nodes: matNodesFromMaterials(materials),
    model,
    duration,
    ratio,
    resolution,
  };
}

export function cloneRegenerateDraft(draft: VideoRegenerateDraft): VideoRegenerateDraft {
  return {
    ...draft,
    prompts: [...draft.prompts],
    nodes: draft.nodes.map((n) => ({ ...n })),
  };
}
